import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  User, Mail, Lock, ChevronLeft, Save, Trash2, 
  Gamepad2, Trophy, Volume2, VolumeX, Palette,
  Eye, EyeOff, Loader2, AlertTriangle, Check
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Profile form
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  
  // Password change
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  
  // Preferences (from localStorage)
  const [preferences, setPreferences] = useState(() => {
    const saved = localStorage.getItem('boosty_preferences');
    return saved ? JSON.parse(saved) : {
      theme: 'cosmic',
      soundEnabled: true,
      showCoordinates: true,
      showLegalMoves: true,
      defaultDifficulty: 'medium'
    };
  });
  
  // Delete account
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    // Fetch user stats
    const fetchStats = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    
    fetchStats();
  }, [isAuthenticated, navigate]);

  const savePreferences = (newPrefs) => {
    setPreferences(newPrefs);
    localStorage.setItem('boosty_preferences', JSON.stringify(newPrefs));
    // Also update the game store settings
    localStorage.setItem('chess_settings', JSON.stringify({
      ...JSON.parse(localStorage.getItem('chess_settings') || '{}'),
      boardTheme: newPrefs.theme,
      soundEnabled: newPrefs.soundEnabled,
      showCoordinates: newPrefs.showCoordinates,
      showLegalMoves: newPrefs.showLegalMoves
    }));
    toast.success('Preferences saved!');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      if (response.ok) {
        toast.success('Password changed successfully!');
        setShowPasswordChange(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/delete-account`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Account deleted');
        await logout();
        navigate('/');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete account');
      }
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Subtle pattern */}
      <div className="fixed inset-0 bg-dot-pattern opacity-30 pointer-events-none" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-obsidian/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="back-to-dashboard"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Dashboard
          </Button>
          <h1 className="font-display text-lg font-semibold text-white">Profile & Settings</h1>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6 relative z-10">
        {/* Profile Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-slate-800 bg-obsidian-card p-6"
        >
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <User className="w-5 h-5 text-cyan-400" />
            Profile Information
          </h2>
          
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center text-3xl font-display font-bold text-white">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
            </div>
            
            {/* Info */}
            <div className="flex-1 space-y-4">
              <div>
                <Label className="text-slate-500 text-xs uppercase tracking-wider">Name</Label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-white text-lg font-medium">{user?.name}</span>
                </div>
              </div>
              <div>
                <Label className="text-slate-500 text-xs uppercase tracking-wider">Email</Label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-slate-300">{user?.email}</span>
                </div>
              </div>
              <div>
                <Label className="text-slate-500 text-xs uppercase tracking-wider">Member Since</Label>
                <div className="text-slate-300 mt-1">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric'
                  }) : 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats Summary */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-xl border border-slate-800 bg-obsidian-card p-6"
          >
            <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              Gaming Stats
            </h2>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="rounded-lg bg-obsidian p-4 text-center border border-slate-800">
                <div className="text-2xl font-display font-bold text-white">{stats.total_games || 0}</div>
                <div className="text-slate-500 text-xs uppercase tracking-wider mt-1">Games</div>
              </div>
              <div className="rounded-lg bg-obsidian p-4 text-center border border-slate-800">
                <div className="text-2xl font-display font-bold text-emerald-400">{stats.wins || 0}</div>
                <div className="text-slate-500 text-xs uppercase tracking-wider mt-1">Wins</div>
              </div>
              <div className="rounded-lg bg-obsidian p-4 text-center border border-slate-800">
                <div className="text-2xl font-display font-bold text-cyan-400">{stats.win_rate || 0}%</div>
                <div className="text-slate-500 text-xs uppercase tracking-wider mt-1">Win Rate</div>
              </div>
              <div className="rounded-lg bg-obsidian p-4 text-center border border-slate-800">
                <div className="text-2xl font-display font-bold text-amber-400">{stats.best_streak || 0}</div>
                <div className="text-slate-500 text-xs uppercase tracking-wider mt-1">Best Streak</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Preferences */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-xl border border-slate-800 bg-obsidian-card p-6"
        >
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Gamepad2 className="w-5 h-5 text-cyan-400" />
            Game Preferences
          </h2>
          
          <div className="space-y-6">
            {/* Theme */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Palette className="w-5 h-5 text-purple-400" />
                <div>
                  <div className="text-white font-medium text-sm">Board Theme</div>
                  <div className="text-slate-500 text-xs">Choose your preferred style</div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={preferences.theme === 'cosmic' ? 'default' : 'outline'}
                  onClick={() => savePreferences({ ...preferences, theme: 'cosmic' })}
                  className={preferences.theme === 'cosmic' ? 'bg-cyan-600 hover:bg-cyan-500' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
                >
                  Cosmic
                </Button>
                <Button
                  size="sm"
                  variant={preferences.theme === 'classic' ? 'default' : 'outline'}
                  onClick={() => savePreferences({ ...preferences, theme: 'classic' })}
                  className={preferences.theme === 'classic' ? 'bg-amber-600 hover:bg-amber-500' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
                >
                  Classic
                </Button>
              </div>
            </div>
            
            {/* Sound */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {preferences.soundEnabled ? (
                  <Volume2 className="w-5 h-5 text-emerald-400" />
                ) : (
                  <VolumeX className="w-5 h-5 text-slate-500" />
                )}
                <div>
                  <div className="text-white font-medium text-sm">Sound Effects</div>
                  <div className="text-slate-500 text-xs">Play sounds for moves and captures</div>
                </div>
              </div>
              <Switch
                checked={preferences.soundEnabled}
                onCheckedChange={(checked) => savePreferences({ ...preferences, soundEnabled: checked })}
              />
            </div>
            
            {/* Show Coordinates */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center text-cyan-400 font-mono text-xs">A1</span>
                <div>
                  <div className="text-white font-medium text-sm">Board Coordinates</div>
                  <div className="text-slate-500 text-xs">Show file and rank labels</div>
                </div>
              </div>
              <Switch
                checked={preferences.showCoordinates}
                onCheckedChange={(checked) => savePreferences({ ...preferences, showCoordinates: checked })}
              />
            </div>
            
            {/* Show Legal Moves */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-cyan-400/30 border border-cyan-400/50" />
                <div>
                  <div className="text-white font-medium text-sm">Legal Move Hints</div>
                  <div className="text-slate-500 text-xs">Highlight possible moves when selecting a piece</div>
                </div>
              </div>
              <Switch
                checked={preferences.showLegalMoves}
                onCheckedChange={(checked) => savePreferences({ ...preferences, showLegalMoves: checked })}
              />
            </div>
            
            {/* Default Difficulty */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Trophy className="w-5 h-5 text-amber-400" />
                <div>
                  <div className="text-white font-medium text-sm">Default AI Difficulty</div>
                  <div className="text-slate-500 text-xs">Starting difficulty for new games</div>
                </div>
              </div>
              <select
                value={preferences.defaultDifficulty}
                onChange={(e) => savePreferences({ ...preferences, defaultDifficulty: e.target.value })}
                className="bg-obsidian border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
                <option value="expert">Expert</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Security */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="rounded-xl border border-slate-800 bg-obsidian-card p-6"
        >
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Lock className="w-5 h-5 text-cyan-400" />
            Security
          </h2>
          
          {!showPasswordChange ? (
            <Button
              variant="outline"
              onClick={() => setShowPasswordChange(true)}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <Lock className="w-4 h-4 mr-2" />
              Change Password
            </Button>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
              <div>
                <Label className="text-slate-400 text-sm">Current Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPasswords ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="bg-slate-800 border-slate-600 text-white pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-gray-400">New Password</Label>
                <Input
                  type={showPasswords ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-slate-800 border-slate-600 text-white mt-1"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <Label className="text-gray-400">Confirm New Password</Label>
                <Input
                  type={showPasswords ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-slate-800 border-slate-600 text-white mt-1"
                  required
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Save Password
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="text-slate-400 hover:text-white hover:bg-slate-800"
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </motion.div>

        {/* Danger Zone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="rounded-xl border border-red-500/20 bg-obsidian-card p-6"
        >
          <h2 className="text-lg font-semibold text-red-400 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Danger Zone
          </h2>
          
          <p className="text-slate-400 text-sm mb-4">
            Once you delete your account, all your data will be permanently removed. This action cannot be undone.
          </p>
          
          {!showDeleteConfirm ? (
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account
            </Button>
          ) : (
            <div className="space-y-4 max-w-md">
              <p className="text-red-400 text-sm">
                Type <strong>DELETE</strong> to confirm:
              </p>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="bg-obsidian border-red-500/30 text-white focus:border-red-500"
                placeholder="Type DELETE"
              />
              <div className="flex gap-3">
                <Button
                  onClick={handleDeleteAccount}
                  disabled={isLoading || deleteConfirmText !== 'DELETE'}
                  className="bg-red-600 hover:bg-red-500"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Permanently Delete'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                  }}
                  className="text-slate-400 hover:text-white hover:bg-slate-800"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};


export default ProfilePage;
